import { type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, type UserRole } from '@/store/authStore';
import { Spinner } from '@/components/ui/Spinner';

interface AuthGuardProps {
  children?: ReactNode;
  /** When set, redirects authenticated users of the wrong role away */
  requireRole?: UserRole;
}

export function AuthGuard({ children, requireRole }: AuthGuardProps) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, role } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" className="text-primary-600" aria-label={t('actions.loading')} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect authenticated users away from wrong-role routes
  if (requireRole && role && role !== requireRole) {
    return <Navigate to={role === 'student' ? '/student' : '/parent'} replace />;
  }

  // Support both wrapper usage (<AuthGuard><Page /></AuthGuard>) and
  // layout route usage (<Route element={<AuthGuard />}><Route … /></Route>)
  return children ? <>{children}</> : <Outlet />;
}
